import { UserRiskFactorsPerCancerDto } from '@/api/generated/model/userRiskFactorsPerCancerDto';
import { UserRiskFactorToCancersImpactDto } from '@/api/generated/model/userRiskFactorToCancersImpactDto';
import { RiskFactorToCancersImpactDto } from '@/api/generated/model/riskFactorToCancersImpactDto';
import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import ModalDetailRiskFactor from '@/components/modal-detail-risk-factor/modal-detail-risk-factor';
import Resources from '@/components/resources/resources';
import TabItem from '@/components/tab-item/tab-item';
import { RISK_LOWER_THRESHOLD, RISK_UPPER_THRESHOLD } from '@/constants/risks';
import { ROUTE_SINGLE_CANCER_VIEW } from '@/constants/routes';
import useRiskFactorModal from '@/hooks/use-risk-factor-modal';
import { useSticky } from '@/hooks/use-sticky';
import { useGetNavbarHeight } from '@/queries/layout.queries';
import { CancerTypeRisk, RiskFactorType } from '@/repositories/risk.repository';
import { colorFromRisk } from '@/utils/cancer/risk/color-from-risk';
import { formatRiskDecimal } from '@/utils/cancer/risk/format-risk-decimal';
import { formatCancerTypeNameWithCapitalization } from '@/utils/cancer/types/format-cancer-type-name-with-capitalization';
import { isNil } from '@/utils/common/is-nil';
import { getRoute } from '@/utils/routing/get-route';
import { Box, Divider, Flex, Tabs, Text, useBreakpointValue } from '@cmpsr/components';
import { useEffect, useRef, useState } from 'react';
import { RiskFactorsBreakdownCard } from '../risk-factors-breakdown-card/risk-factors-breakdown-card';
import CancerMenu from './components/cancer-menu/cancer-menu';
import { RiskFactorBreakdownTableByCancer } from './risk-factor-breakdown-table-by-cancer';
import { RiskFactorBreakdownTableByFactor } from './risk-factor-breakdown-table-by-factor';

const HEADER_PADDING = 20;
const TABS_HEIGHT_MOBILE = 76;
const TABS_HEIGHT_TABLET = 84;
const TABS_HEIGHT_DESKTOP = 92;
const EST_HEADER_HEIGHT_MOBILE = 56 + HEADER_PADDING + TABS_HEIGHT_MOBILE;
const EST_HEADER_HEIGHT_TABLET = 72 + HEADER_PADDING + TABS_HEIGHT_TABLET;
const EST_HEADER_HEIGHT_DESKTOP = 80 + HEADER_PADDING + TABS_HEIGHT_DESKTOP;

const useCardManager = <T extends string>(defaultValues: Partial<Record<T, boolean>> = {}) => {
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { ssr: false }) || false;
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }, { ssr: false }) || false;

  const [cardIsExpanded, setCardIsExpanded] = useState<Partial<Record<T, boolean>>>(defaultValues);

  const toggleCard = (key: T) => {
    setCardIsExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const jumpToCard = (key: T, cardRef: React.RefObject<HTMLDivElement>) => {
    setCardIsExpanded((prev) => ({ ...prev, [key]: true }));
    requestAnimationFrame(() => {
      const anchor = cardRef.current?.querySelector(`[data-anchor-id="${key}"]`);
      if (anchor) {
        const estHeaderOffset = isMobile
          ? EST_HEADER_HEIGHT_MOBILE
          : isTablet
          ? EST_HEADER_HEIGHT_TABLET
          : EST_HEADER_HEIGHT_DESKTOP;
        const elementPosition = anchor.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - estHeaderOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    });
  };

  return [cardIsExpanded, toggleCard, jumpToCard] as const;
};

export const RiskFactorsBreakdown = ({
  riskFactorsByCancer,
  cancersByRiskFactor,
  cancerTypeRiskDataByCancerType,
}: {
  riskFactorsByCancer: UserRiskFactorsPerCancerDto | undefined;
  cancersByRiskFactor: UserRiskFactorToCancersImpactDto | undefined;
  cancerTypeRiskDataByCancerType: Record<CancerTypeRisk['cancer'], CancerTypeRisk> | undefined;
}) => {
  // Scroll to top when the tabs mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const countOfCancers = riskFactorsByCancer?.factorsPerCancer.filter(
    (cancer) => cancer.cancerType !== 'overall'
  ).length;
  const countOfRiskFactors = cancersByRiskFactor?.riskFactorsToCancersImpact.length;

  const cancerListRef = useRef<HTMLDivElement>(null);
  const riskFactorListRef = useRef<HTMLDivElement>(null);

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [cancerCardIsExpanded, toggleCancerCard, jumpToCancerCard] =
    useCardManager<UserRiskFactorsPerCancerDto['factorsPerCancer'][number]['cancerType']>();

  const [riskFactorCardIsExpanded, toggleRiskFactorCard, jumpToRiskFactorCard] =
    useCardManager<UserRiskFactorToCancersImpactDto['riskFactorsToCancersImpact'][number]['id']>();

  const parseValue = (value: string, cancerType?: string) => {
    const parsedValue = parseFloat(value);
    return isNaN(parsedValue) ? value : formatRiskDecimal(parsedValue, cancerType);
  };

  const { data: navBarSize } = useGetNavbarHeight();
  const { ref, isSticky } = useSticky(navBarSize);

  const getRiskAverage = (currentRisk: number | undefined, averageRisk: number | undefined) => {
    if (!currentRisk || !averageRisk) return 'average';

    if (currentRisk > averageRisk * RISK_UPPER_THRESHOLD) return 'above';
    if (currentRisk < averageRisk * RISK_LOWER_THRESHOLD) return 'below';
    return 'average';
  };

  const {
    showModalDetailRiskFactor,
    onHideModalDetailRiskFactor,
    handleOpenModal,
    selectedRiskFactor,
    affectedCancerTypes,
    impactsByCancerType,
  } = useRiskFactorModal();

  const handleOpenRiskFactorModal = (riskFactor: RiskFactorToCancersImpactDto) => {
    return handleOpenModal({
      id: riskFactor.id,
      label: riskFactor.label,
      shortDescription: riskFactor.shortDescription || '',
      detailedDescription: riskFactor.shortDescription || '',
      whyItMatters: riskFactor.whyItMatters || '',
      riskFactorType: riskFactor.riskFactorType as RiskFactorType,
      impact: riskFactor.cancers?.find((c) => c.cancerType === 'overall')?.impact || 0,
      sources: riskFactor.sources,
    });
  };

  return (
    <Tabs variant="line" isLazy width="100%" onChange={setActiveTabIndex}>
      <Box ref={ref} pt={isSticky ? { base: '76px', md: '84px', lg: '92px' } : 'inherit'} />
      <Box
        pt={{ base: 8, md: 10, lg: 12 }}
        {...(isSticky
          ? {
              position: 'fixed',
              top: `${navBarSize}px`,
              px: { base: 'spacer-4', md: 'spacer-8', lg: 'spacer-16', xl: 'spacer-24', xxl: 'spacer-32' },
              left: 0,
              right: 0,
              width: '100%',
              zIndex: 1,
              bgColor: 'background-page',
              boxShadow: 'elevation-interactive',
            }
          : {
              position: 'unset',
            })}
      >
        <Flex justifyContent="space-between" alignItems="center" maxWidth="1184px" margin="0 auto">
          <Tabs.List data-testid="risk-factors-breakdown-tabs">
            <TabItem additionalProps={{ py: 3 }}>
              <Text variant="text-body-meta-medium" textTransform="uppercase" whiteSpace="nowrap">
                Cancers ({countOfCancers})
              </Text>
            </TabItem>
            <TabItem additionalProps={{ py: 3 }}>
              <Text variant="text-body-meta-medium" textTransform="uppercase" whiteSpace="nowrap">
                Risk Factor ({countOfRiskFactors})
              </Text>
            </TabItem>
          </Tabs.List>
          <Box alignSelf="center">
            <CancerMenu
              activeTabIndex={activeTabIndex}
              riskFactorsByCancer={riskFactorsByCancer}
              cancersByRiskFactor={cancersByRiskFactor}
              cancerListRef={cancerListRef}
              riskFactorListRef={riskFactorListRef}
              jumpToCancerCard={jumpToCancerCard}
              jumpToRiskFactorCard={jumpToRiskFactorCard}
            />
          </Box>
        </Flex>
      </Box>
      <Divider />
      <Tabs.Panels>
        <Tabs.Panel px={0} py={0}>
          <DashboardGrid pt={{ base: 5, md: 8, lg: 12 }}>
            <Flex as="main" direction="column" gap={5} ref={cancerListRef}>
              {riskFactorsByCancer?.factorsPerCancer.map((cancer) => (
                <RiskFactorsBreakdownCard
                  key={cancer.cancerType}
                  id={cancer.cancerType}
                  title={formatCancerTypeNameWithCapitalization(cancer.cancerType)}
                  iconId={cancer.cancerType}
                  isExpanded={cancerCardIsExpanded[cancer.cancerType] === true}
                  link={getRoute(ROUTE_SINGLE_CANCER_VIEW, { cancerId: cancer.cancerType })}
                  toggleExpanded={() =>
                    // allow closing the tab by clicking the same cancer type
                    toggleCancerCard(cancer.cancerType)
                  }
                  headerPills={
                    !isNil(cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.risk) &&
                    !isNil(cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.averageRisk)
                      ? [
                          {
                            subtitle: 'Current Lifetime Risk',
                            value: parseValue(
                              cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.risk.toString() ?? '0',
                              cancer.cancerType
                            ),
                            ...colorFromRisk(
                              cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.risk,
                              cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.averageRisk
                            ),
                            testId: `risk-factors-breakdown-card-${cancer.cancerType}-current-risk`,
                          },
                          {
                            subtitle: 'Potential Risk',
                            color: 'primary-default',
                            value: parseValue(
                              cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.potentialRisk.toString() ?? '0',
                              cancer.cancerType
                            ),
                            textColor: 'white',
                            testId: `risk-factors-breakdown-card-${cancer.cancerType}-potential-risk`,
                          },
                          {
                            subtitle: 'Population Average',
                            color: 'tan',
                            value: parseValue(
                              cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.averageRisk.toString() ?? '0',
                              cancer.cancerType
                            ),
                            textColor: 'black',
                            testId: `risk-factors-breakdown-card-${cancer.cancerType}-population-average`,
                          },
                        ]
                      : []
                  }
                  average={getRiskAverage(
                    cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.risk,
                    cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.averageRisk
                  )}
                  risk={cancerTypeRiskDataByCancerType?.[cancer.cancerType]?.risk}
                >
                  <RiskFactorBreakdownTableByFactor cancer={cancer} onRiskFactorClick={handleOpenRiskFactorModal} />
                </RiskFactorsBreakdownCard>
              ))}
            </Flex>
            <Resources as="aside" width="full" />
          </DashboardGrid>
        </Tabs.Panel>
        <Tabs.Panel px={0} py={0}>
          <DashboardGrid pt={{ base: 5, md: 8, lg: 12 }}>
            <Flex as="main" direction="column" gap={5} ref={riskFactorListRef}>
              {cancersByRiskFactor?.riskFactorsToCancersImpact.map((riskFactor) => (
                <RiskFactorsBreakdownCard
                  key={riskFactor.id}
                  id={riskFactor.id}
                  title={riskFactor.label}
                  iconId={riskFactor.id}
                  isExpanded={riskFactorCardIsExpanded[riskFactor.id] === true}
                  toggleExpanded={() =>
                    // allow closing the tab by clicking the same risk factor
                    toggleRiskFactorCard(riskFactor.id)
                  }
                  onOpenRiskFactorModal={() => handleOpenRiskFactorModal(riskFactor)}
                  subtitle={riskFactor.subtitleDescription}
                  hideChildren={riskFactor.riskFactorType.startsWith('non-quantifiable')}
                  headerPills={
                    !isNil(riskFactor.yourValue) && !isNil(riskFactor.populationAverage)
                      ? [
                          {
                            subtitle: 'Your Current Behavior',
                            color: 'primary-default',
                            value: riskFactor.yourValue,
                            textColor: 'white',
                          },
                          {
                            subtitle: 'Population Average Behavior',
                            color: 'tan',
                            value: riskFactor.populationAverage,
                            textColor: 'black',
                          },
                        ]
                      : []
                  }
                  average={getRiskAverage(Number(riskFactor.yourValue), Number(riskFactor.populationAverage))}
                  risk={riskFactor.cancers?.find((c) => c.cancerType === 'overall')?.impact}
                  hideRiskBadge
                >
                  <RiskFactorBreakdownTableByCancer riskFactor={riskFactor} />
                </RiskFactorsBreakdownCard>
              ))}
            </Flex>
            <Resources as="aside" width="full" />
          </DashboardGrid>
        </Tabs.Panel>
      </Tabs.Panels>
      {selectedRiskFactor && (
        <ModalDetailRiskFactor
          isOpen={showModalDetailRiskFactor}
          onClose={onHideModalDetailRiskFactor}
          riskFactor={selectedRiskFactor}
          affectedCancerTypes={affectedCancerTypes}
          impactsByCancerType={impactsByCancerType}
        />
      )}
    </Tabs>
  );
};
